import { create } from "zustand";
import toast from "react-hot-toast";
import { axiosInstance } from "../lib/axios";
import { useAuthStore } from "./useAuthStore";
import axios from "axios";

export interface MessageReaction { userId: string; emoji: string }
export interface ReplyRef { messageId: string; text: string; senderName: string }
export interface MessageEvent {
  kind?: string;
  milestoneIndex?: number | null;
  milestoneTitle?: string;
  amount?: number | null;
  status?: string;
}
export interface Message {
  _id: string;
  senderId: string;
  receiverId: string;
  text?: string;
  file?: FileData | null;
  createdAt?: Date;
  status?: "sending" | "sent" | "delivered" | "failed" | "read";
  reactions?: MessageReaction[];
  replyTo?: ReplyRef | null;
  projectId?: string | null;
  messageType?: "user" | "system";
  event?: MessageEvent | null;
}

export interface User {
  _id: string;
  fullName: string;
  email: string;
  profilePic?: string;
  name: string;
  unreadCount?: number;
  lastMessageAt?: string | null;
}

export interface FileData {
  type?: (fileName: string, type: any) => import("react").ReactNode;
  fileName: string;
  fileSize: number;
  fileUrl: string;
  fileType: string;
}

interface ChatState {
  users: User[];
  messages: Message[];
  selectedUser: User | null;
  isUsersLoading: boolean;
  isMessagesLoading: boolean;

  uploadingFile: { name: string; progress: number } | null;

  getUsers: () => Promise<void>;
  getMessages: (userId: string, projectId?: string | null) => Promise<void>;
  sendMessage: (messageData: {
    text?: string;
    file?: { data: string; fileName: string; fileSize: number } | null;
  }) => Promise<void>;
  uploadFile: (file: File) => Promise<{ fileUrl: string; fileName: string; fileSize: number } | null>;

  subscribeToMessages: () => void;
  unsubscribeFromMessages: () => void;
  subscribeSidebar: () => void;
  unsubscribeSidebar: () => void;
  clearUnread: (userId: string) => void;
  setSelectedUser: (user: User | null) => void;

  // Inbox project context + reactions/replies.
  activeProjectId: string | null;
  setActiveProjectId: (id: string | null) => void;
  replyingTo: Message | null;
  setReplyingTo: (m: Message | null) => void;
  reactMessage: (messageId: string, emoji: string) => Promise<void>;
}

// Sort: unread conversations first, then most-recent activity, then name.
const sortUsers = (list: User[]): User[] =>
  [...list].sort((a, b) => {
    const ua = a.unreadCount || 0;
    const ub = b.unreadCount || 0;
    if (ub !== ua) return ub - ua;
    const at = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
    const bt = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
    if (bt !== at) return bt - at;
    return (a.fullName || "").localeCompare(b.fullName || "");
  });

// Module-level socket handler refs so we can detach precisely (without nuking
// each other's listeners on the shared "newMessage" event).
let chatMsgHandler: ((m: Message) => void) | null = null;
let chatReadHandler: ((p: { readerId: string }) => void) | null = null;
let chatReactionHandler: ((p: { messageId: string; reactions: MessageReaction[] }) => void) | null = null;
let sidebarMsgHandler: ((m: Message) => void) | null = null;
// Ref count — many components (BD layout, chat sidebar, dashboard) share ONE
// socket listener; it stays attached until every subscriber has detached.
let sidebarSubCount = 0;

export const useChatStore = create<ChatState>((set, get) => ({
  users: [],
  messages: [],
  selectedUser: null,
  isUsersLoading: false,
  isMessagesLoading: false,
  activeProjectId: null,
  replyingTo: null,

  uploadingFile: null,

  setActiveProjectId: (id) => set({ activeProjectId: id }),
  setReplyingTo: (m) => set({ replyingTo: m }),

  reactMessage: async (messageId, emoji) => {
    try {
      const { data } = await axiosInstance.post(`/messages/${messageId}/react`, { emoji });
      set({ messages: get().messages.map((m) => (m._id === messageId ? { ...m, reactions: data.reactions } : m)) });
    } catch {
      toast.error("Couldn't react");
    }
  },

  getUsers: async () => {
    set({ isUsersLoading: true });
    try {
      const res = await axiosInstance.get<User[]>("/messages/users");
      set({ users: sortUsers(res.data) });
    } catch (error: unknown) {
      if (error && typeof error === "object" && "response" in error) {
        const err = error as { response?: { data?: { message?: string } } };
        toast.error(err.response?.data?.message || "Failed to load users");
      } else {
        toast.error("An unexpected error occurred");
      }
    } finally {
      set({ isUsersLoading: false });
    }
  },

getMessages: async (userId: string, projectId?: string | null) => {
  set({ isMessagesLoading: true });
  try {
    // Project-scoped thread: only the active project's messages come back.
    const res = await axiosInstance.get<Message[]>(`/messages/${userId}`, {
      params: projectId ? { projectId } : {},
    });
    set({ messages: res.data });
  } catch (error: any) {
    toast.error("Failed to fetch messages");
  } finally {
    set({ isMessagesLoading: false });
  }
},

  uploadFile: async (file: File) => {
    set({ uploadingFile: { name: file.name, progress: 0 } });

    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await axios.post(`/api/upload`, formData, {
        onUploadProgress: (progressEvent) => {
          const percent = Math.round(
            (progressEvent.loaded * 100) / (progressEvent.total || 1)
          );
          set({ uploadingFile: { name: file.name, progress: percent } });
        },
      });

      set({ uploadingFile: null });
      return {
        fileUrl: res.data.fileUrl,
        fileName: file.name,
        fileSize: file.size,
      };
    } catch (error: any) {
      console.error("Upload failed:", error);
      set({ uploadingFile: null });
      toast.error("File upload failed");
      return null;
    }
  },

sendMessage: async (messageData) => {
  const { selectedUser, messages, activeProjectId, replyingTo } = get();
  const authUser = useAuthStore.getState().authUser;

  if (!selectedUser || !authUser) return;

  const replyRef: ReplyRef | undefined = replyingTo
    ? { messageId: replyingTo._id, text: replyingTo.text || (replyingTo.file ? `[${replyingTo.file.fileName}]` : ""), senderName: replyingTo.senderId === authUser._id ? "You" : selectedUser.name || selectedUser.fullName }
    : undefined;

const tempMessage: Message = {
  _id: crypto.randomUUID(),
  senderId: authUser._id,
  receiverId: selectedUser._id,
  projectId: activeProjectId,
  ...(replyRef ? { replyTo: replyRef } : {}),
  ...(messageData.text ? { text: messageData.text } : {}),
  ...(messageData.file
    ? {
        file: {
          fileName: messageData.file.fileName,
          fileSize: messageData.file.fileSize,
          fileUrl: messageData.file.data,
          fileType: messageData.file.fileName.split(".").pop() || "",
        },
      }
    : {}),
  createdAt: new Date(),
  status: "sending",
};

  set({ messages: [...messages, tempMessage], replyingTo: null });

  try {

    const res = await axiosInstance.post<Message>(
      `/messages/send/${selectedUser._id}`,
      { ...messageData, projectId: activeProjectId, ...(replyRef ? { replyTo: replyRef } : {}) }
    );

    set({
      messages: get().messages.map((m) =>
        m._id === tempMessage._id
          ? { ...res.data, status: "sent" }
          : m
      ),
    });
  } catch (error) {

    set({
      messages: get().messages.map((m) =>
        m._id === tempMessage._id
          ? { ...m, status: "failed" }
          : m
      ),
    });

    toast.error("Failed to send message");
  }
},

subscribeToMessages: () => {
  const socket = useAuthStore.getState().socket;
  if (!socket) return;

  if (chatMsgHandler) socket.off("newMessage", chatMsgHandler);
  if (chatReadHandler) socket.off("messagesRead", chatReadHandler);

  chatMsgHandler = (newMessage: Message) => {
    const { messages, activeProjectId, selectedUser } = get();
    const authUser = useAuthStore.getState().authUser;

    // Only append messages that belong to the open conversation…
    if (
      selectedUser &&
      authUser &&
      String(newMessage.senderId) !== String(selectedUser._id) &&
      String(newMessage.receiverId) !== String(selectedUser._id) &&
      String(newMessage.senderId) !== String(authUser._id)
    ) return;
    // …and to the active project (legacy unstamped messages stay visible).
    const msgProject = newMessage.projectId ? String(newMessage.projectId) : null;
    if (activeProjectId && msgProject && msgProject !== String(activeProjectId)) return;

    const alreadyExists = messages.some(
      (m) => m._id === newMessage._id
    );

    set({
      messages: alreadyExists
        ? messages.map((m) =>
            m._id === newMessage._id
              ? { ...newMessage, status: "delivered" }
              : m
          )
        : [...messages, { ...newMessage, status: "delivered" }],
    });
  };
  socket.on("newMessage", chatMsgHandler);

  chatReadHandler = ({ readerId }: { readerId: string }) => {
    const authUser = useAuthStore.getState().authUser;
    if (!authUser) return;

    set({
      messages: get().messages.map((m) =>
        m.senderId === authUser._id &&
        m.receiverId === readerId
          ? { ...m, status: "read" }
          : m
      ),
    });
  };
  socket.on("messagesRead", chatReadHandler);

  if (chatReactionHandler) socket.off("messageReaction", chatReactionHandler);
  chatReactionHandler = ({ messageId, reactions }: { messageId: string; reactions: MessageReaction[] }) => {
    set({ messages: get().messages.map((m) => (m._id === messageId ? { ...m, reactions } : m)) });
  };
  socket.on("messageReaction", chatReactionHandler);
},

  unsubscribeFromMessages: () => {
    const socket = useAuthStore.getState().socket;
    if (chatMsgHandler) socket?.off("newMessage", chatMsgHandler);
    if (chatReadHandler) socket?.off("messagesRead", chatReadHandler);
    if (chatReactionHandler) socket?.off("messageReaction", chatReactionHandler);
    chatMsgHandler = null;
    chatReadHandler = null;
    chatReactionHandler = null;
  },

  // App-wide listener that keeps the contact list fresh: bumps a conversation's
  // unread count + recency when a message arrives for a chat that isn't open.
  subscribeSidebar: () => {
    const socket = useAuthStore.getState().socket;
    if (!socket) return;
    sidebarSubCount += 1;
    if (sidebarMsgHandler) return; // one shared listener is enough

    sidebarMsgHandler = (newMessage: Message) => {
      const authUser = useAuthStore.getState().authUser;
      if (!authUser || newMessage.senderId === authUser._id) return;

      const sender = newMessage.senderId;
      const isOpen = get().selectedUser?._id === sender;
      const nowIso = (newMessage.createdAt
        ? new Date(newMessage.createdAt)
        : new Date()
      ).toISOString();

      const exists = get().users.some((u) => u._id === sender);
      if (!exists) {
        // Unknown contact (e.g. first-ever message) — pull the fresh list.
        get().getUsers();
        return;
      }

      const updated = get().users.map((u) =>
        u._id === sender
          ? {
              ...u,
              lastMessageAt: nowIso,
              unreadCount: isOpen ? 0 : (u.unreadCount || 0) + 1,
            }
          : u
      );
      set({ users: sortUsers(updated) });
    };
    socket.on("newMessage", sidebarMsgHandler);
  },

  unsubscribeSidebar: () => {
    sidebarSubCount = Math.max(0, sidebarSubCount - 1);
    if (sidebarSubCount > 0) return; // other subscribers still need it
    const socket = useAuthStore.getState().socket;
    if (sidebarMsgHandler) socket?.off("newMessage", sidebarMsgHandler);
    sidebarMsgHandler = null;
  },

  clearUnread: (userId: string) => {
    set({
      users: get().users.map((u) =>
        u._id === userId ? { ...u, unreadCount: 0 } : u
      ),
    });
  },

  setSelectedUser: (user: User | null) => {
    set({ selectedUser: user });
    if (user) get().clearUnread(user._id);
  },
}));
